import { CATEGORIES } from "./categories";
import {
  DAY, ROUTES, WEEK_HOURS_CAP, categoryKeyFor, classDate, enrolled, isActive, num, readiness, seats, weeklyHoursByInstructor,
} from "../shared/classData";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmt = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });

/** Maps a course/cert name onto one of the four colour-coded categories. */
export function categoryKey(text = "") {
  return categoryKeyFor(text);
}
const categoryColor = (key) => (CATEGORIES.find((c) => c.k === key) || CATEGORIES[3]).color;

/** Last 12 calendar months, bucketed by class start month. Completions are only charted when the payload carries them. */
function buildTrend(courses, now) {
  const buckets = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - 11 + i, 1);
    return { y: d.getFullYear(), m: d.getMonth(), enrolled: 0, completed: 0, started: 0 };
  });
  courses.forEach((c) => {
    const d = classDate(c);
    const b = d && buckets.find((x) => x.y === d.getFullYear() && x.m === d.getMonth());
    if (!b) return;
    b.enrolled += enrolled(c);
    b.completed += num(c.completedCount);
    b.started += 1;
  });
  const completed = buckets.map((b) => b.completed);
  return {
    labels: buckets.map((b) => MONTHS[b.m]),
    enrolled: buckets.map((b) => b.enrolled),
    completed: completed.some(Boolean) ? completed : null,
    started: buckets.map((b) => b.started),
  };
}

function buildExpiringWeeks(certs, now) {
  return Array.from({ length: 12 }, (_, i) => {
    const start = new Date(+now + i * 7 * DAY);
    const end = new Date(+start + 7 * DAY);
    const v = { fire: 0, ems: 0, aha: 0, res: 0 };
    certs.forEach((c) => {
      const e = new Date(c.expires);
      if (e >= start && e < end) v[categoryKeyFor(c.category, c.name || c.type)] += 1;
    });
    return { l: i % 2 ? "" : fmt(start), range: `${fmt(start)} – ${fmt(new Date(+end - DAY))}`, v };
  });
}

/** Program health = cohort readiness (instructor assigned, seats filling); the row figure is the seat fill. */
function buildHealth(active, now) {
  const rows = active.map((c) => {
    const cap = seats(c);
    return { id: c.id, name: c.title || c.name || "Cohort", sub: `${enrolled(c)}${cap ? ` of ${cap}` : ""} seats filled`, pct: cap ? Math.round(enrolled(c) / cap * 100) : 0, status: readiness(c, now) };
  });
  const counts = rows.reduce((acc, r) => ({ ...acc, [r.status]: acc[r.status] + 1 }), { ok: 0, risk: 0, behind: 0 });
  const rank = { behind: 0, risk: 1, ok: 2 };
  return { counts, rows: [...rows].sort((a, b) => rank[a.status] - rank[b.status] || a.pct - b.pct).slice(0, 4) };
}

function buildLoad(instructors, active, now) {
  const hours = weeklyHoursByInstructor(active, now);
  return instructors.map((i) => {
    const id = i.id || i.userId;
    return { id, n: i.fullName || i.name || i.username || "Instructor", h: Math.round(hours[id] || 0) };
  }).filter((r) => r.h > 0).sort((a, b) => b.h - a.h).slice(0, 5);
}

function buildActions({ expiringCerts, load, pending, awaitingSignoff, active, go, now }) {
  const soon = expiringCerts.filter((c) => new Date(c.expires) <= new Date(+now + 14 * DAY)).length;
  const over = load.filter((r) => r.h > WEEK_HOURS_CAP);
  const unassigned = active.filter((c) => !c.instructorId && !c.leadInstructorId);
  return [
    soon && { id: "certs", tone: "r", icon: "alert", title: `${soon} instructor certification${soon === 1 ? "" : "s"} expire within 14 days`, detail: "Review before they lapse", cta: "Review", onClick: () => go(ROUTES.instructors) },
    ...over.map((r) => ({ id: `over-${r.id}`, tone: "w", icon: "user", title: `${r.n} is over capacity`, detail: `${r.h} of ${WEEK_HOURS_CAP} scheduled hours this week`, cta: "Reassign", onClick: () => go(ROUTES.instructors) })),
    awaitingSignoff > 0 && { id: "signoff", tone: "w", icon: "clip", title: `${awaitingSignoff} skill sheet${awaitingSignoff === 1 ? "" : "s"} awaiting sign-off`, detail: "Across all cohorts", cta: "Open", onClick: () => go(ROUTES.catalog) },
    pending.length && { id: "pending", tone: "w", icon: "clip", title: `${pending.length} course submission${pending.length === 1 ? "" : "s"} awaiting review`, detail: "Course approvals", cta: "Review", onClick: () => go(ROUTES.approvals) },
    unassigned.length && { id: "unassigned", tone: "v", icon: "cert", title: `${unassigned.length} cohort${unassigned.length === 1 ? " has" : "s have"} no instructor`, detail: unassigned[0].title || "Assign an instructor before the start date", cta: "Open", onClick: () => go(ROUTES.instructors) },
  ].filter(Boolean);
}

/** Pure view-model builder for AdminDashboard; takes already-fetched data (see classData.js for the class payload). */
export function buildAdminModel({ loading = false, courses = [], submissions = [], instructors = [], expiringCerts = [], stats = {}, user, tenantConfig, navigate = () => {}, now = new Date() }) {
  const active = courses.filter((c) => isActive(c, now));
  const learners = active.reduce((s, c) => s + enrolled(c), 0);
  const trend = buildTrend(courses, now);
  const half = Math.floor(trend.enrolled.length / 2);
  const prev = trend.enrolled.slice(0, half).reduce((a, b) => a + b, 0);
  const cur = trend.enrolled.slice(half).reduce((a, b) => a + b, 0);
  const expiring = buildExpiringWeeks(expiringCerts, now);
  const load = buildLoad(instructors, active, now);
  const pending = submissions.filter((s) => String(s.status || "").toLowerCase() === "pending");
  const startsSoon = active.filter((c) => classDate(c) > now && classDate(c) <= new Date(+now + 30 * DAY)).length;
  const checklist = stats?.checklistStats || {};
  const graded = num(checklist.total);
  const name = user?.fullName || user?.name || user?.username?.split("@")[0] || "there";
  const cum = trend.enrolled.reduce((acc, v) => [...acc, (acc[acc.length - 1] || 0) + v], []);
  return {
    loading,
    header: {
      eyebrow: `${tenantConfig?.name || "Training Center"} · ${now.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}`,
      greeting: `Welcome back, ${name}`,
      summary: `Overseeing ${active.length} active cohort${active.length === 1 ? "" : "s"} and ${learners} learner${learners === 1 ? "" : "s"}.`,
    },
    kpis: {
      enrollment: learners,
      enrollmentSpark: cum,
      enrollmentDelta: prev ? Math.round((cur - prev) / prev * 100) : null,
      cohorts: active.length,
      cohortMini: trend.started.slice(-5),
      upcomingStarts: startsSoon,
      certs: expiringCerts.length,
      certsSoon: expiringCerts.filter((c) => new Date(c.expires) <= new Date(+now + 14 * DAY)).length,
      certsSpark: expiring.map((w) => Object.values(w.v).reduce((a, b) => a + b, 0)),
      compliance: graded > 0 && checklist.passRate != null ? Number(checklist.passRate) : null,
      graded,
    },
    trend,
    health: buildHealth(active, now),
    expiring,
    load,
    actions: buildActions({ expiringCerts, load, pending, awaitingSignoff: num(stats?.pendingApprovals), active, go: navigate, now }),
    upcoming: [...active].filter((c) => classDate(c) > now).sort((a, b) => classDate(a) - classDate(b)).slice(0, 3).map((c) => {
      const d = classDate(c);
      return { id: c.id, month: d.toLocaleDateString("en-US", { month: "short" }), day: String(d.getDate()).padStart(2, "0"), title: c.title || c.name || "Cohort", color: categoryColor(categoryKeyFor(c.category, c.title || c.name)), filled: enrolled(c), total: seats(c) };
    }),
    navigate,
  };
}
